import { useMemo, useState } from "react";
import { runVendorDirectoryAdminSource } from "../../../../api/vendorDirectorySourceControls";
import { ADMIN_PERMISSIONS } from "../../../../auth/adminPermissions";
import { useAuth } from "../../../../auth/useAuth";
import DataSourceControlsGrid from "../dataSources/DataSourceControlsGrid";
import { mapVendorDirectorySourceToGridRow } from "../dataSources/mapDataSourceGridRow";

export default function VendorDirectorySourceControlsTable({
  sources = [],
  filters = {},
  hideEmpty = false,
  onRefresh,
  showToast,
  setSources,
}) {
  const [busyKey, setBusyKey] = useState(null);
  const { hasPermission, isSuperAdmin } = useAuth();
  const canManageSources =
    isSuperAdmin || hasPermission(ADMIN_PERMISSIONS.REAL_ESTATE_SOURCES_MANAGE);

  const filteredSources = useMemo(() => {
    let list = [...(sources || [])];
    if (filters.search) {
      const query = filters.search.toLowerCase();
      list = list.filter(
        (source) =>
          source.sourceName?.toLowerCase().includes(query) ||
          source.sourceKey?.toLowerCase().includes(query) ||
          source.sourceCode?.toLowerCase().includes(query) ||
          source.connectorKey?.toLowerCase().includes(query)
      );
    }
    if (filters.enabledFilter === "enabled") {
      list = list.filter((source) => source.isActive || source.enabled);
    } else if (filters.enabledFilter === "disabled") {
      list = list.filter((source) => !(source.isActive || source.enabled));
    }
    if (filters.statusFilter && filters.statusFilter !== "all") {
      list = list.filter((source) => {
        const status = String(source.healthStatus || source.health || "").toLowerCase();
        if (filters.statusFilter === "healthy") return status === "healthy";
        if (filters.statusFilter === "failed") {
          return status === "failed" || status === "failing" || status === "blocked";
        }
        if (filters.statusFilter === "warning") {
          return status === "warning" || status === "degraded" || status === "stale";
        }
        if (filters.statusFilter === "never_run") {
          return status === "never_run" || status === "unknown";
        }
        if (filters.statusFilter === "disabled") return status === "disabled";
        if (filters.statusFilter === "running") return status === "running" || status === "queued";
        return status === filters.statusFilter;
      });
    }
    return list.map((source) => mapVendorDirectorySourceToGridRow(source));
  }, [sources, filters]);

  async function handleRunNow(source) {
    if (!canManageSources || busyKey) return;
    const key = source.sourceKey || source.sourceCode;
    setBusyKey(key);
    setSources?.((current) =>
      (current || []).map((row) =>
        (row.sourceKey || row.sourceCode) === key
          ? { ...row, isRunning: true, healthStatus: "running", health: "running" }
          : row
      )
    );

    try {
      const payload = await runVendorDirectoryAdminSource(source.sourceId || key);
      showToast?.(payload.message || `${source.displayName} collection run queued.`, "success");
      await onRefresh?.({ silent: true });
    } catch (error) {
      showToast?.(error.message || "Unable to start vendor directory collection.", "error");
      setSources?.((current) =>
        (current || []).map((row) =>
          (row.sourceKey || row.sourceCode) === key ? { ...row, isRunning: false } : row
        )
      );
    } finally {
      setBusyKey(null);
    }
  }

  if (!filteredSources.length) {
    if (hideEmpty) {
      return null;
    }
    return <p className="muted">No vendor directory sources match the current filters.</p>;
  }

  return (
    <DataSourceControlsGrid
      sources={filteredSources}
      emptyMessage="No vendor directory sources match the current filters."
      busyKey={busyKey}
      canManageSources={canManageSources}
      onRunNow={handleRunNow}
      sectionLabel="Vendors"
      onViewRuns={(source) =>
        showToast?.(
          source.lastRunAt
            ? `${source.displayName} last ran ${source.lastRunAt}.`
            : `${source.displayName} has not been run yet.`,
          "neutral"
        )
      }
      resolveRunNowTitle={(source) =>
        source.supportsRunNow
          ? "Run CasaMapa vendor directory collection now"
          : source.disabledRunNowReason || "This vendor source cannot be run."
      }
    />
  );
}
